/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { BuildingType, CityStats, GameStatus, Grid, NewsItem, RandomEvent, ResearchState } from '../types';
import { UndergroundLayer } from '../types/underground';
import { useGridActions } from './useGridActions';
import { useEconomyHandlers } from './useEconomyHandlers';
import { useSocHandlers } from './useSocHandlers';

export interface UseGameWorldActionsDeps {
  grid: Grid;
  setGrid: Dispatch<SetStateAction<Grid>>;
  stats: CityStats;
  setStats: Dispatch<SetStateAction<CityStats>>;
  research: ResearchState;
  setResearch: Dispatch<SetStateAction<ResearchState>>;
  gameStarted: boolean;
  gameStatus: GameStatus;
  activeLayer: UndergroundLayer;
  addNewsItem: (item: NewsItem) => void;
  activeEvent: RandomEvent | null;
  setActiveEvent: Dispatch<SetStateAction<RandomEvent | null>>;
}

export function useGameWorldActions({
  grid,
  setGrid,
  stats,
  setStats,
  research,
  setResearch,
  gameStarted,
  gameStatus,
  activeLayer,
  addNewsItem,
  activeEvent,
  setActiveEvent,
}: UseGameWorldActionsDeps) {
  const {
    selectedTool,
    setSelectedTool,
    selectedTile,
    setSelectedTile,
    handleTileClick,
    handleUpgrade,
    handleDelete,
    getPlacementError,
  } = useGridActions({
    grid,
    setGrid,
    stats,
    setStats,
    addNewsItem,
    gameStarted,
    gameStatus,
    activeLayer,
  });

  const hasJumpGate = useMemo(
    () => grid.flat().some((t) => t.buildingType === BuildingType.JumpGate && t.isConnected),
    [grid]
  );

  const {
    handleLaunchProduct,
    handleStartResearch,
    handleTakeLoan,
    handleRepayLoan,
    handlePitchInvestors,
  } = useEconomyHandlers({
    grid,
    stats,
    setStats,
    research,
    setResearch,
    hasJumpGate,
    addNewsItem,
  });

  const { handleSOCAction } = useSocHandlers({
    stats,
    setStats,
    hasJumpGate,
    activeEvent,
    setActiveEvent,
    addNewsItem,
  });

  const selectedTileData = selectedTile ? grid[selectedTile.y][selectedTile.x] : null;

  return {
    selectedTool,
    setSelectedTool,
    selectedTile,
    setSelectedTile,
    handleTileClick,
    handleUpgrade,
    handleDelete,
    getPlacementError,
    selectedTileData,
    hasJumpGate,
    handleLaunchProduct,
    handleStartResearch,
    handleTakeLoan,
    handleRepayLoan,
    handlePitchInvestors,
    handleSOCAction,
  };
}